import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import type { RuntimeConfig } from './config';
import { withBackoff } from './retry';

export interface VerifySuccess {
  success: true;
  data: {
    sessionToken: string;
    expiresAt?: string | number;
    sessionId?: string;
    user?: {
      id?: number;
      telegramId?: number;
      username?: string;
      languageCode?: string;
    };
  };
  traceId?: string;
}

export interface VerifyError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
  traceId?: string;
}

export type VerifyResponse = VerifySuccess | VerifyError;

export interface TaskDTO {
  id: string;
  title: string;
  due?: string;
  status: 'pending' | 'done';
}

export interface ReminderDTO {
  id: string;
  title: string;
  scheduledAt: string;
  status: 'pending' | 'done';
}

export interface SettingsDTO {
  timezone: string;
}

interface SessionSnapshot {
  sessionToken?: string;
  userId?: number;
  telegramUserId?: number;
}

export interface MiniAppApi {
  verify(initData: string, meta?: Record<string, unknown>): Promise<VerifyResponse>;
  getTasks(userId?: string | number): Promise<TaskDTO[]>;
  // Write operations disabled - read-only mode
  createTask(payload: { title: string; due?: string }): Promise<never>;
  updateTask(id: string, payload: Partial<Pick<TaskDTO, 'title' | 'due' | 'status'>>): Promise<TaskDTO>;
  deleteTask(id: string): Promise<{ success: boolean; message?: string }>;
  getReminders(userId?: string | number): Promise<ReminderDTO[]>;
  // Write operations disabled - read-only mode
  createReminder(payload: { title: string; scheduledAt: string }): Promise<never>;
  updateReminder(
    id: string,
    payload: Partial<Pick<ReminderDTO, 'title' | 'scheduledAt' | 'status'>>
  ): Promise<never>;
  deleteReminder(id: string): Promise<never>;
  // Settings endpoint
  getSettings(userId?: string | number): Promise<SettingsDTO>;
  updateSettings(payload: SettingsDTO): Promise<never>;
}

export function createMiniAppApi(
  config: RuntimeConfig,
  getSessionSnapshot: () => SessionSnapshot
): MiniAppApi {
  const client: AxiosInstance = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 8000,
    headers: {
      'Content-Type': 'application/json',
      'X-Build-Version': config.buildVersion,
      ...(config.telegramSecretToken && {
        'X-Telegram-Bot-Api-Secret-Token': config.telegramSecretToken,
      }),
    },
  });

  client.interceptors.request.use(request => {
    const { sessionToken: token } = getSessionSnapshot();
    if (token) {
      const headers = AxiosHeaders.from(request.headers);
      headers.set('Authorization', `Bearer ${token}`);
      request.headers = headers;
    }
    window.dispatchEvent(
      new CustomEvent('webapp:network', {
        detail: {
          url: request.url ?? request.baseURL,
          method: request.method?.toUpperCase() ?? 'GET',
          status: undefined,
        },
      })
    );
    return request;
  });

  client.interceptors.response.use(
    response => {
      window.dispatchEvent(
        new CustomEvent('webapp:network', {
          detail: {
            url: response.config.url ?? response.request?.responseURL,
            method: response.config.method?.toUpperCase() ?? 'GET',
            status: response.status,
            traceId: response.headers['x-trace-id'],
          },
        })
      );
      return response;
    },
    error => {
      const { config: req, response } = error;
      window.dispatchEvent(
        new CustomEvent('webapp:network', {
          detail: {
            url: req?.url ?? req?.baseURL,
            method: req?.method?.toUpperCase() ?? 'GET',
            status: response?.status ?? 0,
            error: error.message,
            traceId: response?.headers?.['x-trace-id'],
          },
        })
      );
      return Promise.reject(error);
    }
  );

  async function requestWithRetry<T>(fn: () => Promise<T>): Promise<T> {
    return withBackoff(fn, { attempts: 3, baseDelayMs: 1000 });
  }

  function buildReadonlyParams(explicitUserId?: string | number): Record<string, string> {
    const snapshot = getSessionSnapshot();
    const params: Record<string, string> = {};
    const primaryId = explicitUserId ?? snapshot.userId ?? snapshot.telegramUserId;
    const telegramId =
      snapshot.telegramUserId ??
      (typeof explicitUserId === 'number' ? explicitUserId : undefined);

    if (typeof primaryId === 'number' || typeof primaryId === 'string') {
      params.userId = String(primaryId);
    }
    if (typeof telegramId === 'number') {
      params.telegramId = String(telegramId);
    }

    if (!params.userId && !params.telegramId) {
      console.warn('[MiniAppApi] Read-only request without user identifier');
    }

    return params;
  }

  return {
    async verify(initData, meta = {}) {
      return requestWithRetry(async () => {
        try {
          const body = {
            initData,
            ...meta,
          };
          
          if (body.initData) {
            const checksum = Array.from(body.initData).reduce((acc, char) => {
              return (acc + char.charCodeAt(0)) >>> 0;
            }, 0);
            console.log('[PAYLOAD_INITDATA]', {
              length: body.initData.length,
              checksum: checksum.toString(16),
            });
          } else {
            console.log('[PAYLOAD_INITDATA]', { length: 0 });
          }
          
          const response = await client.post('/api/webapp/auth/telegram/verify', body);
          return response.data as VerifyResponse;
        } catch (error) {
          // Handle network errors (timeout, connection issues, CORS)
          if (axios.isAxiosError(error)) {
            if (error.response?.data) {
              // Server returned an error response (401, 400, etc.)
              return error.response.data as VerifyResponse;
            }
            // Network error (no response) - create a friendly error response
            const networkError: VerifyError = {
              success: false,
              error: {
                code: error.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK_ERROR',
                message: error.code === 'ECONNABORTED' 
                  ? 'Превышено время ожидания ответа сервера'
                  : 'Ошибка подключения к серверу. Проверьте интернет-соединение.',
              },
            };
            return networkError;
          }
          // Unknown error - rethrow
          throw error;
        }
      });
    },
    async getTasks(userId?: string | number) {
      return requestWithRetry(async () => {
        try {
          const params = buildReadonlyParams(userId);
          const response = await client.get('/api/webapp/readonly/tasks', { params });
          return response.data?.data ?? [];
        } catch (error) {
          // Don't mask backend errors (4xx/5xx) as network errors
          if (axios.isAxiosError(error) && error.response) {
            // Backend returned an error response - let React Query handle it
            throw error;
          }
          // Real network error (no response) - throw as-is
          throw error;
        }
      });
    },
    // Write operations disabled - read-only mode
    async createTask(payload) {
      throw new Error('Write operations are disabled. This is a read-only webapp. Use the Telegram bot to create tasks.');
    },
    async updateTask(id: string, payload: Partial<Pick<TaskDTO, 'title' | 'due' | 'status'>>) {
      return requestWithRetry(async () => {
        try {
          const { sessionToken } = getSessionSnapshot();
          if (!sessionToken) {
            throw new Error('Session token required for write operations');
          }
          const response = await client.patch(`/api/webapp/tasks/${id}`, payload);
          return response.data?.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.data) {
            throw error;
          }
          throw error;
        }
      });
    },
    async deleteTask(id: string) {
      return requestWithRetry(async () => {
        try {
          const { sessionToken } = getSessionSnapshot();
          if (!sessionToken) {
            throw new Error('Session token required for write operations');
          }
          const response = await client.delete(`/api/webapp/tasks/${id}`);
          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.data) {
            throw error;
          }
          throw error;
        }
      });
    },
    async getReminders(userId?: string | number) {
      return requestWithRetry(async () => {
        try {
          const params = buildReadonlyParams(userId);
          const response = await client.get('/api/webapp/readonly/reminders', { params });
          return response.data?.data ?? [];
        } catch (error) {
          // Don't mask backend errors (4xx/5xx) as network errors
          if (axios.isAxiosError(error) && error.response) {
            // Backend returned an error response - let React Query handle it
            throw error;
          }
          // Real network error (no response) - throw as-is
          throw error;
        }
      });
    },
    // Write operations disabled - read-only mode
    async createReminder(payload) {
      throw new Error('Write operations are disabled. This is a read-only webapp. Use the Telegram bot to create reminders.');
    },
    async updateReminder(id, payload) {
      throw new Error('Write operations are disabled. This is a read-only webapp. Use the Telegram bot to update reminders.');
    },
    async deleteReminder(id) {
      throw new Error('Write operations are disabled. This is a read-only webapp. Use the Telegram bot to delete reminders.');
    },
    // Settings endpoint
    async getSettings(userId?: string | number) {
      return requestWithRetry(async () => {
        try {
          const params = buildReadonlyParams(userId);
          const response = await client.get('/api/webapp/readonly/settings', { params });
          return response.data?.data ?? { timezone: 'UTC' };
        } catch (error) {
          // Don't mask backend errors (4xx/5xx) as network errors
          if (axios.isAxiosError(error) && error.response) {
            // Backend returned an error response - let React Query handle it
            throw error;
          }
          // Real network error (no response) - throw as-is
          throw error;
        }
      });
    },
    async updateSettings(payload) {
      throw new Error('Write operations are disabled. This is a read-only webapp. Use the Telegram bot to update settings.');
    },
  };
}
